import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { HeartHandshake, Mail, Phone, Building2, Trash2, Plus, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { country: "", organization: "", email: "", phone: "", note: "" };

export default function AdminLiaisonsPage() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/liaisons");
      setItems(data);
    } catch {
      toast.error("Could not load liaisons");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
  }, [refresh]);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const onEdit = (l) => {
    setEditingId(l.id);
    setForm({ country: l.country || "", organization: l.organization || "", email: l.email || "", phone: l.phone || "", note: l.note || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.country || !form.organization) {
      toast.error("Country and organisation are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null, phone: form.phone || null, note: form.note || null };
      if (editingId) {
        await api.put(`/admin/liaisons/${editingId}`, payload);
        toast.success("Liaison updated");
      } else {
        await api.post("/admin/liaisons", payload);
        toast.success("Liaison added");
      }
      resetForm();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
    setSaving(false);
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/admin/liaisons/${id}`);
      toast.success("Liaison removed");
      if (editingId === id) resetForm();
      refresh();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-liaisons-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · contacts</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white">
          <HeartHandshake size={18} />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Regional liaisons</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        Families are directed to a regional liaison instead of seeing case comparisons. Configure one contact
        per country.
      </p>

      <form onSubmit={onSubmit} className="mt-8 rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="liaison-form">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{editingId ? "Edit liaison" : "Add a liaison"}</h2>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="liaison-cancel-edit">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          )}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Country *</Label>
            <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
              <SelectTrigger className="mt-1 h-11" data-testid="liaison-country-trigger"><SelectValue placeholder="Select a country" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Organisation *</Label>
            <Input className="mt-1 h-11" value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} placeholder="e.g. Federal Police - Missing Persons Unit" data-testid="liaison-organization-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input className="mt-1 h-11" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="liaison@example.eu" data-testid="liaison-email-input" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input className="mt-1 h-11" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+32 2 123 4567" data-testid="liaison-phone-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Note (optional)</Label>
            <Input className="mt-1 h-11" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Office hours, languages spoken, etc." data-testid="liaison-note-input" />
          </div>
        </div>
        <div className="mt-5">
          <Button type="submit" disabled={saving} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="liaison-submit-btn">
            <Plus size={16} className="mr-1" /> {editingId ? "Save changes" : "Add liaison"}
          </Button>
        </div>
      </form>

      <div className="mt-8 space-y-3" data-testid="liaisons-list">
        {loading && <p className="font-body text-sm text-[#5C6873]" data-testid="liaisons-loading">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center font-body text-sm text-[#5C6873]" data-testid="liaisons-empty">
            No liaisons configured yet.
          </div>
        )}
        {items.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid={`liaison-row-${l.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-[#4A7C59]/30 bg-[#4A7C59]/10 px-2 py-0.5 font-body text-xs text-[#4A7C59]">{l.country}</span>
                <span className="inline-flex items-center gap-1 font-heading text-base font-semibold text-[#1A2024]"><Building2 size={14} /> {l.organization}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-body text-sm text-[#5C6873]">
                {l.email && <span className="inline-flex items-center gap-1"><Mail size={13} /> {l.email}</span>}
                {l.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {l.phone}</span>}
              </div>
              {l.note && <p className="mt-1 font-body text-xs text-[#5C6873]">{l.note}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={() => onEdit(l)} data-testid={`liaison-edit-${l.id}`}>
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="text-[#9E4747] hover:bg-[#9E4747]/10" onClick={() => onDelete(l.id)} data-testid={`liaison-delete-${l.id}`}>
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
